/**
 * Phase 15.6N — Community Activity Feed.
 *
 * Vertical feed of recent platform events: lives, uploads, spotlight
 * wins, crew joins, verifications, major rewards, collaborations.
 * Mounted on Home and Creator Hub. Auto-refreshes every 90s.
 *
 * Props:
 *   limit?: number (default 12)
 *   compact?: boolean — denser layout for Creator Hub side rail
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { optimizeImage } from "@/lib/mediaUrls";

const TYPE_META = {
  live_started: { tint: "border-red-500/30 hover:border-red-400/50", label: "went live" },
  track_uploaded: { tint: "border-purple-500/25 hover:border-purple-400/50", label: "dropped" },
  spotlight_won: { tint: "border-amber-400/40 hover:border-amber-300/60", label: "won Spotlight" },
  crew_joined: { tint: "border-fuchsia-500/25 hover:border-fuchsia-400/50", label: "joined" },
  verified: { tint: "border-emerald-400/30 hover:border-emerald-300/50", label: "verified" },
  major_reward: { tint: "border-yellow-400/30 hover:border-yellow-300/50", label: "earned" },
  collab_added: { tint: "border-pink-500/30 hover:border-pink-400/50", label: "collab'd" },
};

const _timeAgo = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!isFinite(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const CommunityActivityFeed = ({ limit = 12, compact = false }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchFeed = async () => {
      try {
        const r = await api.get("/activity/community-feed", { params: { limit } });
        if (!cancelled) {
          setEvents(r.data?.events || []);
          setLoading(false);
        }
      } catch (_e) {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFeed();
    const id = setInterval(fetchFeed, 90000);
    return () => { cancelled = true; clearInterval(id); };
  }, [limit]);

  if (loading) {
    return (
      <Card className="p-4 bg-white/5 border-purple-500/15" data-testid="community-activity-feed-loading">
        <p className="text-purple-200/60 text-sm">Loading community activity…</p>
      </Card>
    );
  }
  if (events.length === 0) {
    return null;
  }

  return (
    <section className={compact ? "" : "mb-6"} data-testid="community-activity-feed">
      <div className="flex items-baseline justify-between mb-3 px-1">
        <h2 className="text-white font-bold text-base flex items-center gap-2">
          <span>🌐</span> Community Activity
        </h2>
        <span className="text-[11px] text-purple-200/60 tabular-nums" data-testid="community-activity-count">
          {events.length} recent
        </span>
      </div>
      <ul className="space-y-2" data-testid="community-activity-list">
        {events.map((e, idx) => {
          const meta = TYPE_META[e.type] || { tint: "border-purple-500/15", label: e.type };
          const actorHref = e.actor_user_id ? `/artist/${e.actor_user_id}` : "#";
          return (
            <li key={`${e.type}-${idx}-${e.ts || idx}`}>
              <div
                className={`flex items-center gap-3 ${compact ? "p-2.5" : "p-3"} rounded-lg bg-white/[0.03] border ${meta.tint} transition-colors`}
                data-testid={`community-activity-item-${e.type}-${idx}`}
              >
                <div className={`flex-shrink-0 ${compact ? "w-8 h-8 text-base" : "w-10 h-10 text-lg"} rounded-full bg-purple-500/20 flex items-center justify-center`}>
                  {e.icon || "✨"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-white ${compact ? "text-xs" : "text-sm"} truncate`}>
                    <Link
                      to={actorHref}
                      className="font-semibold hover:text-purple-300"
                      data-testid={`community-activity-actor-${idx}`}
                    >
                      {e.actor_name}
                    </Link>
                    <span className="text-purple-200/60"> {meta.label} </span>
                    {e.link ? (
                      <Link
                        to={e.link}
                        className="font-semibold hover:text-pink-300"
                        data-testid={`community-activity-link-${idx}`}
                      >
                        {e.title}
                      </Link>
                    ) : (
                      <span className="font-semibold">{e.title}</span>
                    )}
                  </p>
                  {e.subtext && (
                    <p className="text-[11px] text-purple-200/50 truncate">{e.subtext}</p>
                  )}
                  <p className="text-[10px] text-purple-200/40 tabular-nums mt-0.5">{_timeAgo(e.ts)}</p>
                </div>
                {!compact && e.cover_url && (
                  <Link to={e.link || "#"} className="flex-shrink-0">
                    <img
                      src={optimizeImage(e.cover_url, 'thumb')}
                      alt=""
                      className="w-12 h-12 rounded object-cover border border-white/10"
                      onError={(ev) => { ev.target.style.display = "none"; }}
                    />
                  </Link>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default CommunityActivityFeed;
